import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { BOOKS, Book } from "@/config/books";
import BookRequestDialog from "./BookRequestDialog";
import PaperbackOffer from "./PaperbackOffer";

export const BookCover = ({ book, className = "" }: { book: Book; className?: string }) => (
  <img
    src={book.cover}
    alt={`${book.title} by John Mastroianni, book cover`}
    width={book.coverWidth}
    height={book.coverHeight}
    loading="lazy"
    className={`w-full h-auto object-contain shadow-[0_18px_40px_-20px_hsl(0_0%_0%/0.55)] ${className}`}
  />
);

export const GetBookButton = ({ book, className = "" }: { book: Book; className?: string }) => (
  <BookRequestDialog
    defaultBook={book.key}
    trigger={
      <button
        type="button"
        className={`inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 sm:px-5 py-3.5 text-[11px] font-semibold tracking-[0.12em] sm:tracking-[0.2em] uppercase hover:bg-primary/90 transition-all ${className}`}
      >
        <BookOpen className="h-4 w-4 shrink-0" /> Get Your Free Digital Copy
      </button>
    }
  />
);

/** The four free books, with the in-person paperback offer underneath. */
const BooksSection = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== "#free-books") return;
    // Instant jumps after the route's scroll-to-top and lazy content settle.
    const go = () => {
      const el = document.getElementById("free-books");
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 110, behavior: "auto" });
    };
    const a = setTimeout(go, 450);
    const b = setTimeout(go, 1200);
    return () => {
      clearTimeout(a);
      clearTimeout(b);
    };
  }, [hash]);

  return (
    <section id="free-books" className="py-16 md:py-24 bg-secondary/40 border-b border-border scroll-mt-32">
      <div className="container mx-auto max-w-[1200px] px-6">
        <div className="text-center mb-12 md:mb-14">
          <p className="eyebrow mb-4">Free Books by John</p>
          <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight">
            Read John's books <span className="italic text-primary">free, online.</span>
          </h2>
          <div className="gold-divider mx-auto mt-6 mb-6" />
          <p className="text-muted-foreground font-light leading-relaxed max-w-2xl mx-auto">
            Five guides from forty years of New Jersey real estate. Pick a book, tell us where to reach you, and your
            free digital copy opens right away.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-10 sm:gap-x-8 sm:gap-y-12">
          {BOOKS.filter((b) => !b.spread).map((book) => (
            <article
              key={book.key}
              className="grid grid-cols-[112px_minmax(0,1fr)] gap-x-5 items-start sm:flex sm:flex-col"
            >
              <div className="w-full sm:mx-auto sm:max-w-[200px] sm:mb-6">
                <BookCover book={book} />
              </div>
              <div className="flex flex-col min-w-0 sm:flex-1 sm:w-full">
                <p className="eyebrow-sm text-primary mb-2 sm:text-center">{book.audience}</p>
                <h3 className="font-serif text-lg md:text-xl font-light text-foreground leading-snug sm:text-center mb-2 sm:mb-3">
                  {book.title}
                </h3>
                <p className="text-sm text-muted-foreground font-light leading-relaxed sm:text-center mb-4 sm:mb-5 sm:flex-1">
                  {book.description}
                </p>
                <GetBookButton book={book} className="w-full" />
              </div>
            </article>
          ))}
        </div>

        {BOOKS.filter((b) => b.spread).map((book) => (
          <article
            key={book.key}
            className="mt-14 md:mt-16 grid md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] gap-8 md:gap-12 items-center border-t border-border pt-14 md:pt-16"
          >
            <div className="w-full max-w-[620px] mx-auto">
              <BookCover book={book} />
            </div>
            <div className="text-center md:text-left">
              <p className="eyebrow-sm text-primary mb-2">{book.audience}</p>
              <h3 className="font-serif text-2xl md:text-3xl font-light text-foreground leading-snug mb-3">
                {book.title}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground font-light leading-relaxed mb-6">
                {book.description}
              </p>
              <GetBookButton book={book} className="w-full sm:w-auto" />
            </div>
          </article>
        ))}

        <PaperbackOffer className="mt-14 md:mt-16" />
      </div>
    </section>
  );
};

export default BooksSection;
